import React, { useState } from "react";
import Box from "@mui/material/Box";
import {
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import JoditReact from "jodit-react-ts";
import "jodit/build/jodit.min.css";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ICategory from "../../entities/ICategory";
import IUser from "../../entities/IUser";
import IShop from "../../entities/IShop";
import { theme } from "../../theme";

const customerLabel = (u?: IUser) => {
  if (!u) return "";
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.username || u.emailId || `Customer #${u.userId}`;
};

type Props = {
  formik: any;
  categoryData: Array<ICategory>;
  disabled?: boolean;
  onCategoryChange: (categoryId: number) => void;
  customers?: Array<IUser>;
  loadingCustomers?: boolean;
  shops?: Array<IShop>;
  loadingShops?: boolean;
  onCustomerChange?: (customerId: number) => void;
};

const BasicDetailsStep = ({
  formik,
  categoryData,
  disabled = false,
  onCategoryChange,
  customers = [],
  loadingCustomers = false,
  shops = [],
  loadingShops = false,
  onCustomerChange,
}: Props) => {
  const { values, touched, errors, handleChange, handleBlur } = formik;
  // Track only an explicit "user clicked edit" override. Whether the
  
  
  
  
  
  
  
  
  const [manualEdit, setManualEdit] = useState(false);
  const editingCategory = manualEdit || !values.categoryId;

  const selectedCategory = categoryData.find(
    (c: any) => c.categoryId === Number(values.categoryId)
  );

  const handleProductDetail = (content: string) => {
    formik.setFieldValue("product_detail", content);
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Select User
      </Typography>
      <FormControl
        fullWidth
        error={touched.customerId && Boolean(errors.customerId)}
        sx={{ mb: 2.5 }}
      >
        <Select
          displayEmpty
          disabled={disabled || loadingCustomers}
          value={values.customerId || ""}
          onChange={(e) => {
            const customerId = Number(e.target.value);
            formik.setFieldValue("customerId", customerId);
            
            
            formik.setFieldValue("shopId", 0);
            onCustomerChange?.(customerId);
          }}
          renderValue={(value) =>
            value ? (
              customerLabel(customers.find((c) => c.userId === Number(value))) ||
              `Customer #${value}`
            ) : (
              <Typography color="text.secondary">
                {loadingCustomers ? "Loading users…" : "Choose a user"}
              </Typography>
            )
          }
        >
          {customers.map((c) => (
            <MenuItem key={c.userId} value={c.userId}>
              {customerLabel(c)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Select Shop
      </Typography>
      <FormControl
        fullWidth
        error={touched.shopId && Boolean(errors.shopId)}
        sx={{ mb: 2.5 }}
      >
        <Select
          displayEmpty
          disabled={disabled || loadingShops || !values.customerId}
          value={values.shopId || ""}
          onChange={(e) => formik.setFieldValue("shopId", Number(e.target.value))}
          renderValue={(value) =>
            value ? (
              shops.find((s) => s.shopId === Number(value))?.shopName || `Shop #${value}`
            ) : (
              <Typography color="text.secondary">
                {!values.customerId
                  ? "Select a user first"
                  : loadingShops
                  ? "Loading shops…"
                  : shops.length === 0
                  ? "This user has no shops"
                  : "Choose a shop"}
              </Typography>
            )
          }
        >
          {shops.map((s) => (
            <MenuItem key={s.shopId} value={s.shopId}>
              {s.shopName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Category
      </Typography>
      {editingCategory ? (
        <FormControl fullWidth error={touched.categoryId && Boolean(errors.categoryId)} sx={{ mb: 2.5 }}>
          <Select
            displayEmpty
            value={values.categoryId || ""}
            disabled={disabled}
            onChange={(e) => {
              const catId = Number(e.target.value);
              onCategoryChange(catId);
              setManualEdit(false);
            }}
            renderValue={(value) =>
              value ? (
                categoryData.find((c: any) => c.categoryId === Number(value))?.title || ""
              ) : (
                <Typography color="text.secondary">Browse all categories</Typography>
              )
            }
          >
            {categoryData.map((cat: any) => (
              <MenuItem key={cat.categoryId} value={cat.categoryId}>
                {cat.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Box
          onClick={() => !disabled && setManualEdit(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "10px",
            px: 2,
            py: 1.5,
            mb: 2.5,
            cursor: disabled ? "default" : "pointer",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CategoryRoundedIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
            <Typography fontWeight={600}>
              {selectedCategory?.title || "No category selected"}
            </Typography>
          </Box>
          {!disabled && <EditRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />}
        </Box>
      )}

      <TextField
        fullWidth
        disabled={disabled}
        label="Product Title"
        name="title"
        placeholder="Product Title"
        value={values.title}
        onBlur={handleBlur}
        onChange={handleChange}
        error={touched.title && Boolean(errors.title)}
        helperText={touched.title && errors.title}
        sx={{ mb: 2.5 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Highlights
      </Typography>
      <TextField
        fullWidth
        multiline
        minRows={3}
        disabled={disabled}
        name="description"
        placeholder="Key selling points, e.g. Noise cancelling, 30hr battery life"
        value={values.description}
        onBlur={handleBlur}
        onChange={handleChange}
        error={touched.description && Boolean(errors.description)}
        helperText={touched.description && errors.description}
        sx={{ mb: 2.5 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Description
      </Typography>
      <Box sx={{ mb: 2.5 }}>
        <JoditReact
          defaultValue={values.product_detail || ""}
          onChange={handleProductDetail}
          config={{ readonly: disabled }}
        />
      </Box>

      <TextField
        fullWidth
        disabled={disabled}
        label="Brand Name"
        name="brand"
        value={values.brand}
        onBlur={handleBlur}
        onChange={handleChange}
        error={touched.brand && Boolean(errors.brand)}
        helperText={touched.brand && errors.brand}
        sx={{ mb: 1 }}
      />
    </Box>
  );
};

export default BasicDetailsStep;
